/**
 * The ONE parental gate (MONETIZATION-PLAN P0-3) used before every grown-up surface: the paywall,
 * the grown-ups corner, onboarding ("a grown-up is here"), Settings links, external links (Terms,
 * Privacy, store / subscription pages, gift page), sharing.
 *
 *   <ParentalGate context="paywall" onPass onCancel />          full page (paper card on the night desk)
 *   <ParentalGate variant="modal" … />                           over the current screen
 *   const gate = useParentalGate(); gate.guard('terms', () => …); {gate.element}
 *   openExternal(url, guard)                                     external links only behind the gate
 *
 * Challenge: two-digit addition (37 + 48) also written in words in the parent's language, typed on a
 * number pad (or the keyboard). 3 wrong → 60 s lockout, escalating. Passing keeps it open 10 minutes.
 * Logs parental_gate_shown | pass | fail {context}.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useApp } from '../../state/store';
import { useT } from '../../lib/i18n';
import { play } from '../../lib/sfx';
import { buzz } from '../../lib/haptics';
import { trackMoney } from '../../lib/analytics';
import { TeacherJuno } from '../../components/characters';
import { Icon } from '../../components/icons';
import { GATE_MAX_TRIES, gateOpen, makeChallenge, numberWords, openGate, readLock, recordFail, recordPass } from './gate';
import './parents.css';

export { gateOpen } from './gate';

export interface ParentalGateProps {
  /** Where the gate is shown (analytics enum: paywall, parents, onboarding, terms, store, gift, …). */
  context: string;
  onPass: () => void;
  onCancel: () => void;
  variant?: 'page' | 'modal';
  /** Optional override of the explanation line. */
  body?: string;
  cancelLabel?: string;
  testId?: string;
}

const DEV = import.meta.env.DEV;
const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'del', '0', 'ok'] as const;

export function ParentalGate({ context, onPass, onCancel, variant = 'page', body, cancelLabel, testId = 'pgate' }: ParentalGateProps) {
  const t = useT();
  const lang = useApp((s) => s.settings.language);
  const [q, setQ] = useState(() => makeChallenge());
  const [typed, setTyped] = useState('');
  const [wrong, setWrong] = useState(false);
  const [lock, setLock] = useState(readLock);
  const [now, setNow] = useState(Date.now);
  const shown = useRef(false);
  const locked = lock.until > now;
  const secondsLeft = Math.max(0, Math.ceil((lock.until - now) / 1000));

  useEffect(() => {
    if (shown.current) return;
    shown.current = true;
    trackMoney('parental_gate_shown', { context });
  }, [context]);

  // Lockout countdown.
  useEffect(() => {
    if (!locked) return;
    const iv = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(iv);
  }, [locked]);

  const submit = useCallback(() => {
    if (locked || !typed) return;
    if (Number(typed) === q.answer) {
      recordPass();
      openGate();
      trackMoney('parental_gate_pass', { context });
      play('unlock');
      buzz('success');
      onPass();
      return;
    }
    const l = recordFail();
    const nowMs = Date.now();
    trackMoney('parental_gate_fail', { context, lockout: l.until > nowMs });
    play('disabled');
    buzz('error');
    setLock(l);
    setNow(nowMs);
    setWrong(true);
    setTyped('');
    setQ(makeChallenge());
  }, [locked, typed, q.answer, context, onPass]);

  const press = useCallback(
    (k: (typeof KEYS)[number]) => {
      if (locked) return;
      if (k === 'ok') return submit();
      play('click');
      if (k === 'del') setTyped((s) => s.slice(0, -1));
      else setTyped((s) => (s.length >= 3 ? s : s + k));
    },
    [locked, submit],
  );

  // Physical keyboard (grown-ups on a laptop / tablet keyboard).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (/^[0-9]$/.test(e.key)) press(e.key as (typeof KEYS)[number]);
      else if (e.key === 'Backspace') press('del');
      else if (e.key === 'Enter') press('ok');
      else if (e.key === 'Escape') onCancel();
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [press, onCancel]);

  const triesLeft = GATE_MAX_TRIES - lock.fails;
  const card = (
    <div className="par-gate-card pgate-card sb-paper-card" role="dialog" aria-modal={variant === 'modal'} aria-labelledby="pgate-title">
      <div className="par-gate-juno pgate-juno" aria-hidden>
        <TeacherJuno face="thinking" size={116} />
      </div>
      <div className="col pgate-left">
        <span className="par-gate-lock" aria-hidden>
          <Icon name="lock" size={20} />
        </span>
        <h1 id="pgate-title">{t('gate.title')}</h1>
        <p className="par-muted pgate-body">{body ?? t('gate.body')}</p>
        {locked ? (
          <p className="pgate-locked" role="alert" data-testid="pgate-locked" data-seconds={secondsLeft}>
            {t('gate.locked', { s: secondsLeft })}
          </p>
        ) : (
          <>
            <p className="pgate-q" data-testid="pgate-question" data-answer={DEV ? q.answer : undefined}>
              {q.a} + {q.b} = <span className={`pgate-typed${typed ? '' : ' empty'}`} data-testid="pgate-input">{typed || '?'}</span>
            </p>
            <p className="pgate-words">{t('gate.words', { a: numberWords(q.a, lang), b: numberWords(q.b, lang) })}</p>
            {wrong && (
              <p className="form-error pgate-wrong" role="alert" data-testid="pgate-wrong">
                {t('gate.wrong', { n: triesLeft })}
              </p>
            )}
          </>
        )}
        <button type="button" className="par-link" onClick={onCancel} data-testid="pgate-back">
          {cancelLabel ?? t('gate.back')}
        </button>
      </div>
      <div className={`pgate-pad${locked ? ' off' : ''}`} role="group" aria-label={t('gate.pad')}>
        {KEYS.map((k) => (
          <button
            key={k}
            type="button"
            className={`pgate-key${k === 'ok' ? ' ok' : k === 'del' ? ' del' : ''}`}
            disabled={locked || (k === 'ok' && !typed)}
            aria-label={k === 'ok' ? t('gate.ok') : k === 'del' ? t('gate.del') : k}
            data-testid={`pgate-key-${k}`}
            onClick={() => press(k)}
          >
            {k === 'ok' ? <Icon name="check" size={20} /> : k === 'del' ? <Icon name="back" size={18} /> : k}
          </button>
        ))}
      </div>
    </div>
  );

  if (variant === 'modal')
    return (
      <div className="pgate-overlay" data-testid={testId} data-context={context}>
        {card}
      </div>
    );
  return (
    <div className="par-gate pgate-page" data-testid={testId} data-context={context}>
      {card}
    </div>
  );
}

/**
 * Run an action behind the gate: immediately when it was passed < 10 min ago, else after the gate.
 * Render `element` somewhere in the tree.
 */
export function useParentalGate(): { guard: (context: string, fn: () => void) => void; element: ReactNode } {
  const [pending, setPending] = useState<{ context: string; fn: () => void } | null>(null);
  const guard = useCallback((context: string, fn: () => void) => {
    if (gateOpen()) fn();
    else setPending({ context, fn });
  }, []);
  const element = useMemo(
    () =>
      pending ? (
        <ParentalGate
          variant="modal"
          context={pending.context}
          onPass={() => {
            const fn = pending.fn;
            setPending(null);
            fn();
          }}
          onCancel={() => setPending(null)}
        />
      ) : null,
    [pending],
  );
  return { guard, element };
}

/** Open an external page (system browser on native). Callers wrap this in `guard(...)`. */
export function openExternal(url: string): void {
  try {
    window.open(url, '_blank', 'noopener');
  } catch {
    location.assign(url);
  }
}
